import { ReactNode } from 'react';
import { Plus, X } from 'lucide-react';

export interface ChipDef {
	value: string;
	label: string;
	count?: number;
}

/**
 * Saved-view chip row that sits below the page title. HubSpot calls these "views".
 * Each chip is a saved filter. The active chip is highlighted; others are subdued
 * and show an `x` to close (clear) the view when active.
 */
export function FilterChipsBar({
	chips,
	activeValue,
	onSelect,
	onAddView,
	totalViewsLabel,
	allViewsHref
}: {
	chips: ChipDef[];
	activeValue: string;
	onSelect: (value: string) => void;
	onAddView?: () => void;
	totalViewsLabel?: string;
	allViewsHref?: string;
}) {
	return (
		<div className="flex flex-wrap items-center gap-2">
			{chips.map((c) => {
				const active = c.value === activeValue;
				return (
					<button
						key={c.value}
						type="button"
						onClick={() => onSelect(c.value)}
						className={`inline-flex items-center gap-1.5 rounded-md border px-3 py-1.5 text-xs font-medium transition-colors ${active
							? 'border-primary bg-primary/5 text-primary'
							: 'border-input bg-background text-muted-foreground hover:bg-muted'}`}
					>
						<span>{c.label}</span>
						{typeof c.count === 'number' && (
							<span className={active ? 'text-primary' : 'text-muted-foreground'}>({c.count})</span>
						)}
						{active && c.value !== chips[0]?.value && (
							<X
								className="ml-1 h-3 w-3"
								onClick={(e) => {
									e.stopPropagation();
									if (chips[0]) onSelect(chips[0].value);
								}}
							/>
						)}
					</button>
				);
			})}
			{onAddView && (
				<button
					type="button"
					onClick={onAddView}
					className="inline-flex items-center gap-1 rounded-md px-2 py-1.5 text-xs font-medium text-primary hover:bg-primary/10"
				>
					<Plus className="h-3.5 w-3.5" /> Add view {totalViewsLabel ? <span className="text-muted-foreground">{totalViewsLabel}</span> : null}
				</button>
			)}
			{allViewsHref && (
				<a href={allViewsHref} className="text-xs font-medium text-primary hover:underline">All Views</a>
			)}
		</div>
	);
}

/** Pill-style toolbar slot wrapper used between filter chips and the data table. */
export function ListToolbarRow({ children }: { children: ReactNode }) {
	return <div className="flex flex-wrap items-center gap-2">{children}</div>;
}
